import { useState, useEffect } from "react";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";
import toast from "react-hot-toast";

export interface CronData {
    _id: string;
    subject_id: {
        _id: string;
        languageName: string;
        courseName: string;
        standard: string;
        subject: string;
    };
    cron_status: "NotYet" | "Running" | "Stopped";
    start: number;
    end: number;
    time: string | null;
    createdAt: string;
    updatedAt: string;
}

const useCronSettings = () => {
    const [cronData, setCronData] = useState<CronData[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>("");

    // Debounced search effect
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            fetchData(searchQuery);
        }, 500); // 500ms debounce delay

        return () => clearTimeout(debounceTimer);
    }, [searchQuery]);

    const fetchData = async (search?: string) => {
        setLoading(true);
        try {
            let endpoint = apiPaths.getCronSettings;

            // Add search query parameter if provided
            if (search && search.trim().length > 0) {
                endpoint = `${apiPaths.getCronSettings}?q=${encodeURIComponent(
                    search.trim()
                )}`;
            }

            const result = await ApiService({
                method: "GET",
                endpoint: endpoint,
            });

            setCronData(result.data || result || []);
        } catch (error) {
            console.error("Error fetching cron data:", error);
            toast.error("Failed to fetch cron settings");
        } finally {
            setLoading(false);
        }
    };

    const handleStart = async (id: string) => {
        // Find the cron item to check if time is set
        const cronItem = cronData.find(item => item._id === id);

        if (!cronItem?.time || cronItem.time.trim() === "") {
            toast.error("Please set the time before starting the cron job");
            return;
        }

        setLoading(true);
        try {
            await ApiService({
                method: "PUT",
                endpoint: `${apiPaths.updateCronSetting}/${id}`,
                data: { cron_status: "Running" },
            });

            // Update local state
            setCronData((prevData) =>
                prevData.map((item) =>
                    item._id === id ? { ...item, cron_status: "Running" } : item
                )
            );
            toast.success("Cron started successfully");
        } catch (error) {
            console.error("Error starting cron:", error);
            toast.error("Failed to start cron");
        } finally {
            setLoading(false);
        }
    };

    const handleStop = async (id: string) => {
        setLoading(true);
        try {
            await ApiService({
                method: "PUT",
                endpoint: `${apiPaths.updateCronSetting}/${id}`,
                data: { cron_status: "Stopped" },
            });

            // Update local state
            setCronData((prevData) =>
                prevData.map((item) =>
                    item._id === id ? { ...item, cron_status: "Stopped" } : item
                )
            );
            toast.success("Cron stopped successfully");
        } catch (error) {
            console.error("Error stopping cron:", error);
            toast.error("Failed to stop cron");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = async (id: string) => {
        setLoading(true);
        try {
            await ApiService({
                method: "DELETE",
                endpoint: `${apiPaths.resetCron}/${id}`,
            });

            // Refresh data after reset
            await fetchData(searchQuery);
            toast.success("Cron reset successfully");
        } catch (error) {
            console.error("Error resetting cron:", error);
            toast.error("Failed to reset cron");
        } finally {
            setLoading(false);
        }
    };

    return {
        cronData,
        loading,
        searchQuery,
        setSearchQuery,
        handleStart,
        handleStop,
        handleReset,
        fetchData,
    };
};

export default useCronSettings;
